// ============================================
// Creator Editor - Custom Description Module (ES6)
// 전체화면 커스텀 설명 에디터
// ============================================

import { sanitizeHtml, escapeHtml } from '../../utils.js';
import { showToast } from '../../utils.js';
import { updateTabCheckStatus, updatePublishButtonState } from './events.js';

// ============================================
// 상태 관리
// ============================================

let isEditorOpen = false;
let isEventsBound = false;

// ============================================
// 에디터 열기/닫기
// ============================================

/**
 * 커스텀 설명 에디터 열기
 */
export function openCustomDescEditor() {
  const modal = document.getElementById('customDescEditorModal');
  const textarea = document.getElementById('customDescTextarea');
  const hiddenInput = document.getElementById('editorCharDescription');

  if (!modal || !textarea) return;

  // 현재 값 로드
  textarea.value = hiddenInput?.value || '';
  updateCharCount();

  // 편집 탭으로 초기화
  switchCustomDescTab('edit');

  // 모달 열기
  modal.classList.add('active');
  isEditorOpen = true;

  // textarea에 포커스
  setTimeout(() => textarea.focus(), 100);
}

/**
 * 커스텀 설명 에디터 닫기
 */
export function closeCustomDescEditor() {
  const modal = document.getElementById('customDescEditorModal');
  if (!modal) return;

  modal.classList.remove('active');
  isEditorOpen = false;
}

/**
 * 커스텀 설명 저장
 */
export function saveCustomDesc() {
  const textarea = document.getElementById('customDescTextarea');
  const hiddenInput = document.getElementById('editorCharDescription');
  const counterEl = document.getElementById('charDescriptionCount');

  if (!textarea || !hiddenInput) return;

  const value = textarea.value.trim();

  // hidden input에 저장
  hiddenInput.value = value;

  // 메인 에디터의 카운터 업데이트
  if (counterEl) {
    counterEl.textContent = value.length;
  }

  // 상태 메시지 업데이트
  updateCustomDescStatus();

  // 버튼 텍스트 업데이트
  updateCustomDescButton();

  // 유효성 검사 상태 업데이트
  updateTabCheckStatus();
  updatePublishButtonState();

  // 에디터 닫기
  closeCustomDescEditor();

  showToast('커스텀 설명이 저장되었습니다', 'success');
}

// ============================================
// 탭 전환
// ============================================

/**
 * 커스텀 설명 에디터 탭 전환
 * @param {string} tabName - 'edit' 또는 'preview'
 */
export function switchCustomDescTab(tabName) {
  // 탭 버튼 활성화
  document.querySelectorAll('[data-custom-desc-tab]').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.customDescTab === tabName);
  });

  // 탭 콘텐츠 전환
  const editTab = document.getElementById('customDescEditTab');
  const previewTab = document.getElementById('customDescPreviewTab');

  if (editTab) editTab.classList.toggle('active', tabName === 'edit');
  if (previewTab) previewTab.classList.toggle('active', tabName === 'preview');

  // 미리보기 탭일 경우 렌더링
  if (tabName === 'preview') {
    renderPreview();
  }
}

// ============================================
// 미리보기 렌더링
// ============================================

/**
 * 미리보기 렌더링
 */
function renderPreview() {
  const textarea = document.getElementById('customDescTextarea');
  const preview = document.getElementById('customDescPreview');

  if (!textarea || !preview) return;

  const value = textarea.value.trim();

  if (!value) {
    preview.innerHTML = '<p class="preview-empty">미리보기할 내용이 없습니다</p>';
    return;
  }

  // HTML 태그 포함 여부 확인
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(value);
  let rendered;
  if (hasHtmlTags) {
    // HTML 있음: sanitize
    rendered = sanitizeHtml(value);
  } else {
    // 일반 텍스트: escape 후 줄바꿈 변환
    rendered = escapeHtml(value).replace(/\n/g, '<br>');
  }
  preview.innerHTML = rendered;
}

// ============================================
// 글자 수 카운터
// ============================================

/**
 * 글자 수 카운터 업데이트
 */
function updateCharCount() {
  const textarea = document.getElementById('customDescTextarea');
  const counter = document.getElementById('customDescCount');

  if (!textarea || !counter) return;

  counter.textContent = textarea.value.length.toLocaleString();
}

// ============================================
// 상태 메시지 업데이트
// ============================================

/**
 * 커스텀 설명 상태 메시지 업데이트
 */
export function updateCustomDescStatus() {
  const hiddenInput = document.getElementById('editorCharDescription');
  const statusEl = document.getElementById('customDescriptionStatus');

  if (!statusEl) return;

  const hasDesc = hiddenInput?.value?.trim()?.length > 0;

  if (hasDesc) {
    statusEl.innerHTML = `
      <span class="status-badge success">커스텀 설명이 있어요</span>
      <span class="status-text">프롬프트가 공개되지 않아요</span>
    `;
    statusEl.className = 'editor-section-desc has-custom-desc';
  } else {
    statusEl.innerHTML = `
      <span class="status-badge warning">커스텀 설명이 없어요</span>
      <span class="status-text">프롬프트가 사용자에게 공개돼요</span>
    `;
    statusEl.className = 'editor-section-desc no-custom-desc';
  }
}

/**
 * 커스텀 설명 버튼 텍스트 업데이트
 */
export function updateCustomDescButton() {
  const hiddenInput = document.getElementById('editorCharDescription');
  const btn = document.getElementById('openCustomDescEditorBtn');

  if (!btn) return;

  const hasDesc = hiddenInput?.value?.trim()?.length > 0;
  btn.textContent = hasDesc ? '커스텀 설명 수정' : '커스텀 설명 추가';
}

// ============================================
// 이벤트 바인딩
// ============================================

/**
 * 커스텀 설명 에디터 이벤트 바인딩 (한 번만 실행)
 */
export function bindCustomDescEvents() {
  // 이미 바인딩되었으면 스킵
  if (isEventsBound) return;
  isEventsBound = true;

  // 에디터 열기 버튼
  const openBtn = document.getElementById('openCustomDescEditorBtn');
  if (openBtn) {
    openBtn.addEventListener('click', openCustomDescEditor);
  }

  // 탭 전환 버튼
  document.querySelectorAll('[data-custom-desc-tab]').forEach(btn => {
    btn.addEventListener('click', () => {
      switchCustomDescTab(btn.dataset.customDescTab);
    });
  });

  // 글자 수 카운터
  const textarea = document.getElementById('customDescTextarea');
  if (textarea) {
    textarea.addEventListener('input', updateCharCount);
  }

  // 닫기/저장 버튼 (data-action 사용)
  const modal = document.getElementById('customDescEditorModal');
  if (modal) {
    modal.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]')?.dataset.action;
      if (action === 'closeCustomDescEditor') {
        closeCustomDescEditor();
      } else if (action === 'saveCustomDesc') {
        saveCustomDesc();
      }
    });

    // 백드롭 클릭으로 닫기 (선택적)
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeCustomDescEditor();
      }
    });
  }
}

// ============================================
// 초기화
// ============================================

/**
 * 커스텀 설명 모듈 초기화
 */
export function initCustomDesc() {
  bindCustomDescEvents();
  updateCustomDescStatus();
  updateCustomDescButton();
  updateMainCharCounter();
}

/**
 * 정보 탭의 글자 수 카운터 업데이트
 */
function updateMainCharCounter() {
  const hiddenInput = document.getElementById('editorCharDescription');
  const counterEl = document.getElementById('charDescriptionCount');

  if (hiddenInput && counterEl) {
    counterEl.textContent = (hiddenInput.value?.length || 0).toLocaleString();
  }
}

// ============================================
// 제작자 코멘트 에디터
// ============================================

let isCreatorCommentEditorOpen = false;
let isCreatorCommentEventsBound = false;

/**
 * 제작자 코멘트 에디터 열기
 */
export function openCreatorCommentEditor() {
  const modal = document.getElementById('creatorCommentEditorModal');
  const textarea = document.getElementById('creatorCommentTextarea');
  const hiddenInput = document.getElementById('editorCreatorComment');

  if (!modal || !textarea) return;

  // 현재 값 로드
  textarea.value = hiddenInput?.value || '';
  updateCreatorCommentCharCount();

  // 편집 탭으로 초기화
  switchCreatorCommentTab('edit');

  // 모달 열기
  modal.classList.add('active');
  isCreatorCommentEditorOpen = true;

  // textarea에 포커스
  setTimeout(() => textarea.focus(), 100);
}

/**
 * 제작자 코멘트 에디터 닫기
 */
export function closeCreatorCommentEditor() {
  const modal = document.getElementById('creatorCommentEditorModal');
  if (!modal) return;

  modal.classList.remove('active');
  isCreatorCommentEditorOpen = false;
}

/**
 * 제작자 코멘트 저장
 */
export function saveCreatorComment() {
  const textarea = document.getElementById('creatorCommentTextarea');
  const hiddenInput = document.getElementById('editorCreatorComment');
  const counterEl = document.getElementById('creatorCommentCount');

  if (!textarea || !hiddenInput) return;

  const value = textarea.value.trim();

  // hidden input에 저장
  hiddenInput.value = value;

  // 메인 에디터의 카운터 업데이트
  if (counterEl) {
    counterEl.textContent = value.length.toLocaleString();
  }

  // 버튼 텍스트 업데이트
  updateCreatorCommentButton();

  // 등록 버튼 상태 업데이트
  updatePublishButtonState();

  // 에디터 닫기
  closeCreatorCommentEditor();

  showToast('제작자 코멘트가 저장되었습니다', 'success');
}

/**
 * 제작자 코멘트 에디터 탭 전환
 * @param {string} tabName - 'edit' 또는 'preview'
 */
export function switchCreatorCommentTab(tabName) {
  // 탭 버튼 활성화
  document.querySelectorAll('[data-creator-comment-tab]').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.creatorCommentTab === tabName);
  });

  // 탭 콘텐츠 전환
  const editTab = document.getElementById('creatorCommentEditTab');
  const previewTab = document.getElementById('creatorCommentPreviewTab');

  if (editTab) editTab.classList.toggle('active', tabName === 'edit');
  if (previewTab) previewTab.classList.toggle('active', tabName === 'preview');

  // 미리보기 탭일 경우 렌더링
  if (tabName === 'preview') {
    renderCreatorCommentPreview();
  }
}

/**
 * 제작자 코멘트 미리보기 렌더링
 */
function renderCreatorCommentPreview() {
  const textarea = document.getElementById('creatorCommentTextarea');
  const preview = document.getElementById('creatorCommentPreview');

  if (!textarea || !preview) return;

  const value = textarea.value.trim();

  if (!value) {
    preview.innerHTML = '<p class="preview-empty">미리보기할 내용이 없습니다</p>';
    return;
  }

  // HTML 태그 포함 여부 확인
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(value);
  let rendered;
  if (hasHtmlTags) {
    // HTML 있음: sanitize
    rendered = sanitizeHtml(value);
  } else {
    // 일반 텍스트: escape 후 줄바꿈 변환
    rendered = escapeHtml(value).replace(/\n/g, '<br>');
  }
  preview.innerHTML = rendered;
}

/**
 * 제작자 코멘트 글자 수 카운터 업데이트
 */
function updateCreatorCommentCharCount() {
  const textarea = document.getElementById('creatorCommentTextarea');
  const counter = document.getElementById('creatorCommentCharCount');

  if (!textarea || !counter) return;

  counter.textContent = textarea.value.length.toLocaleString();
}

/**
 * 제작자 코멘트 버튼 텍스트 업데이트
 */
export function updateCreatorCommentButton() {
  const hiddenInput = document.getElementById('editorCreatorComment');
  const btn = document.getElementById('openCreatorCommentEditorBtn');

  if (!btn) return;

  const hasComment = hiddenInput?.value?.trim()?.length > 0;
  btn.textContent = hasComment ? '제작자 코멘트 수정' : '제작자 코멘트 추가';
}

/**
 * 제작자 코멘트 이벤트 바인딩 (한 번만 실행)
 */
export function bindCreatorCommentEvents() {
  // 이미 바인딩되었으면 스킵
  if (isCreatorCommentEventsBound) return;
  isCreatorCommentEventsBound = true;

  // 에디터 열기 버튼
  const openBtn = document.getElementById('openCreatorCommentEditorBtn');
  if (openBtn) {
    openBtn.addEventListener('click', openCreatorCommentEditor);
  }

  // 탭 전환 버튼
  document.querySelectorAll('[data-creator-comment-tab]').forEach(btn => {
    btn.addEventListener('click', () => {
      switchCreatorCommentTab(btn.dataset.creatorCommentTab);
    });
  });

  // 글자 수 카운터
  const textarea = document.getElementById('creatorCommentTextarea');
  if (textarea) {
    textarea.addEventListener('input', updateCreatorCommentCharCount);
  }

  // 닫기/저장 버튼 (data-action 사용)
  const modal = document.getElementById('creatorCommentEditorModal');
  if (modal) {
    modal.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]')?.dataset.action;
      if (action === 'closeCreatorCommentEditor') {
        closeCreatorCommentEditor();
      } else if (action === 'saveCreatorComment') {
        saveCreatorComment();
      }
    });

    // 백드롭 클릭으로 닫기
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeCreatorCommentEditor();
      }
    });
  }
}

/**
 * 제작자 코멘트 모듈 초기화
 */
export function initCreatorComment() {
  bindCreatorCommentEvents();
  updateCreatorCommentButton();
  updateCreatorCommentMainCounter();
}

/**
 * 소개 탭의 제작자 코멘트 글자 수 카운터 업데이트
 */
function updateCreatorCommentMainCounter() {
  const hiddenInput = document.getElementById('editorCreatorComment');
  const counterEl = document.getElementById('creatorCommentCount');

  if (hiddenInput && counterEl) {
    counterEl.textContent = (hiddenInput.value?.length || 0).toLocaleString();
  }
}
